/**
 * Store Selector Component
 * Displays nearest store and allows store selection based on user location
 */
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemText,
  ListItemButton,
  Divider,
  useTheme,
} from '@mui/material';
import {
  LocationOn as LocationOnIcon,
  Store as StoreIcon,
  Navigation as NavigationIcon,
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { formatDistance } from '../../services/locationService';

const StoreSelector = ({
  nearestStore,
  selectedStore,
  userLocation,
  stores = [],
  isLoadingStores = false,
  storeError = null,
  onSelectStore,
  onLocationUpdate,
  compact = false,
  showChangeStoreButton = true,
  enableStoreSelection = true,
}) => {
  const theme = useTheme();
  const [showStoreList, setShowStoreList] = useState(false);

  // Get store status (open/closed)
  const getStoreStatus = (store) => {
    if (!store?.timing) {
      return { isOpen: false, status: 'Closed', color: 'error' };
    }

    const now = new Date();
    const currentDay = now.toLocaleDateString('en-US', { weekday: 'short' });
    const currentTime = now.getTime();

    const todayTiming = store.timing.find(
      (timing) => timing.day === currentDay
    );

    if (!todayTiming?.slots?.length) {
      return { isOpen: false, status: 'Closed', color: 'error' };
    }

    const isOpen = todayTiming.slots.some((slot) => {
      const openTime = slot.open_time?.toDate?.()?.getTime() || 0;
      const closeTime = slot.close_time?.toDate?.()?.getTime() || 0;
      return currentTime >= openTime && currentTime <= closeTime;
    });

    return {
      isOpen,
      status: isOpen ? 'Open' : 'Closed',
      color: isOpen ? 'success' : 'error',
    };
  };

  // Format store address for display
  const formatStoreAddress = (store) => {
    const address = store?.address;
    if (!address) return 'Address not available';

    if (address.displayName) {
      return address.displayName;
    }

    const parts = [
      address.streetNumber,
      address.route,
      address.city,
      address.state,
      address.zipCode,
    ].filter(Boolean);

    return parts.join(', ') || 'Address not available';
  };

  // Get stores sorted by distance
  const storesByDistance = stores
    .filter((store) => store.distance !== undefined)
    .sort((a, b) => (a.distance || 0) - (b.distance || 0));

  const handleStoreSelection = (store) => {
    onSelectStore?.(store);
    setShowStoreList(false);
  };

  if (storeError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        <Typography variant="body2">{storeError}</Typography>
        <Button size="small" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (isLoadingStores) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Finding nearest stores...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!selectedStore && !nearestStore) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          {userLocation
            ? 'No stores found in your area.'
            : 'Please allow location access to find nearby stores.'}
        </Typography>
        {!userLocation && onLocationUpdate && (
          <Button size="small" onClick={onLocationUpdate} sx={{ mt: 1 }}>
            Get My Location
          </Button>
        )}
      </Alert>
    );
  }

  const displayStore = selectedStore || nearestStore;
  const storeStatus = getStoreStatus(displayStore);
  const distance = displayStore?.distance;

  return (
    <>
      <Card
        sx={{
          mb: 2,
          border: selectedStore
            ? `2px solid ${theme.palette.primary.main}`
            : undefined,
          ...(compact && {
            '& .MuiCardContent-root': {
              padding: theme.spacing(2),
              '&:last-child': { paddingBottom: theme.spacing(2) },
            },
          }),
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {/* Store Icon */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: '12px',
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                flexShrink: 0,
              }}
            >
              <StoreIcon />
            </Box>

            {/* Store Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}
              >
                <Typography
                  variant={compact ? 'body1' : 'h6'}
                  component="h3"
                  sx={{
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {displayStore?.businessName || 'Store Name'}
                </Typography>

                <Chip
                  size="small"
                  label={storeStatus.status}
                  color={storeStatus.color}
                  sx={{ minWidth: 'auto' }}
                />
              </Box>

              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <LocationOnIcon
                  sx={{ fontSize: 16, color: 'text.secondary' }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatStoreAddress(displayStore)}
                </Typography>
              </Box>

              {distance !== undefined && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NavigationIcon
                    sx={{ fontSize: 16, color: 'text.secondary' }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {formatDistance(distance)} away
                    {!userLocation?.precise && ' (approximate)'}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Actions */}
            {showChangeStoreButton &&
              enableStoreSelection &&
              storesByDistance.length > 1 && (
                <IconButton
                  onClick={() => setShowStoreList(true)}
                  sx={{ flexShrink: 0 }}
                  title="Change store"
                >
                  <ExpandMoreIcon />
                </IconButton>
              )}
          </Box>

          {!compact && selectedStore === displayStore && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
              <Typography variant="body2" color="success.main">
                Selected as your store
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Store Selection Dialog */}
      <Dialog
        open={showStoreList}
        onClose={() => setShowStoreList(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Choose Your Store
          {userLocation && (
            <Typography variant="body2" color="text.secondary">
              Stores near your location
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <List>
            {storesByDistance.map((store, index) => {
              const status = getStoreStatus(store);
              const isSelected = selectedStore?.id === store.id;

              return (
                <React.Fragment key={store.id}>
                  <ListItemButton
                    onClick={() => handleStoreSelection(store)}
                    selected={isSelected}
                    disabled={!status.isOpen}
                  >
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: isSelected ? 600 : 400 }}
                          >
                            {store.businessName}
                          </Typography>
                          <Chip
                            size="small"
                            label={status.status}
                            color={status.color}
                          />
                          {isSelected && (
                            <CheckIcon
                              sx={{ fontSize: 16, color: 'primary.main' }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {formatStoreAddress(store)}
                          </Typography>
                          {store.distance !== undefined && (
                            <Typography variant="body2" color="text.secondary">
                              {formatDistance(store.distance)} away
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                  {index < storesByDistance.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StoreSelector;
